<script lang="ts">
	import { goto } from '$app/navigation';
	import {
		Command,
		CommandEmpty,
		CommandGroup,
		CommandInput,
		CommandItem,
		CommandList
	} from '@repo/ui/src/components/ui/command';
	import {
		Dialog,
		DialogClose,
		DialogOverlay,
		DialogPortal,
		DialogPrimitive
	} from '@repo/ui/src/components/ui/dialog';
	import IonIcon from '@repo/ui/src/components/ui/IonIcon.svelte';

	interface Props {
		open: boolean;
		onOpenChange: (open: boolean) => void;
		docsOnly?: boolean;
	}

	let { open = $bindable(false), onOpenChange, docsOnly = false }: Props = $props();

	let query = $state('');

	// Define searchable pages
	const sitemapPages = [
		// Help/Docs pages
		{
			title: 'User Guide Home',
			url: '/help',
			category: 'Documentation',
			description: 'Master your second brain with Retain'
		},
		{
			title: 'Introduction',
			url: '/help/article/introduction',
			category: 'Documentation',
			description: 'Discover how Retain becomes your productivity superpower'
		},
		{
			title: 'Quick Start Guide',
			url: '/help/article/quick-start',
			category: 'Documentation',
			description: 'Start capturing, organizing, and acting in minutes'
		},
		{
			title: 'Installation',
			url: '/help/article/retain-app-installation-guide',
			category: 'Documentation',
			description: 'Get Retain running on your Mac in seconds'
		},
		{
			title: 'Calendar Integration',
			url: '/help/article/retain-app-calendar-guide',
			category: 'Documentation',
			description: 'Connect your calendar and let Retain automatically capture meeting notes'
		},
		{
			title: 'Email Management',
			url: '/help/article/retain-app-email-management-guide',
			category: 'Documentation',
			description: 'Draft, send, and organize emails with AI assistance'
		},
		{
			title: 'Meeting Capture',
			url: '/help/article/retain-app-how-to-capture-meetings-with-retain',
			category: 'Documentation',
			description: 'Record and transcribe meetings, automatically generate summaries'
		},
		{
			title: 'Email Expertise',
			url: '/help/article/write-more-effective-emails-expertise',
			category: 'Documentation',
			description: 'Learn advanced techniques for writing more effective emails'
		},
		{
			title: 'AI Assistant',
			url: '/help/article/ai-assistant',
			category: 'Documentation',
			description: 'Use AI to organize, remember, and act on your information'
		},
		{
			title: 'Workspaces',
			url: '/help/article/workspaces',
			category: 'Documentation',
			description: 'Organize your work into separate spaces'
		},
		{
			title: 'Integrations',
			url: '/help/article/integrations',
			category: 'Documentation',
			description: 'Connect Retain with your favorite tools'
		},
		{
			title: 'Account Management',
			url: '/help/article/retain-app-account-management',
			category: 'Documentation',
			description: 'Manage your Retain account settings'
		},
		{
			title: 'FAQ',
			url: '/help/article/faq',
			category: 'Documentation',
			description: 'Frequently asked questions about Retain'
		},

		// Main site pages
		{
			title: 'Home',
			url: '/',
			category: 'Pages',
			description: 'The only productivity app you will ever need'
		},
		{
			title: 'Blog',
			url: '/blog',
			category: 'Pages',
			description: 'Latest updates and insights from Retain'
		},
		{
			title: 'Download',
			url: '/download',
			category: 'Pages',
			description: 'Download Retain for Mac'
		},
		{
			title: 'Sign Up',
			url: '/sign-up',
			category: 'Pages',
			description: 'Create your Retain account'
		},
		{
			title: 'Sign In',
			url: '/sign-in',
			category: 'Pages',
			description: 'Sign in to your account'
		},
		{
			title: 'Privacy Policy',
			url: '/privacy-policy',
			category: 'Pages',
			description: 'How we protect your data'
		},
		{
			title: 'Terms of Service',
			url: '/terms-of-service',
			category: 'Pages',
			description: 'Terms and conditions'
		}
	];

	// Filter pages based on query and docsOnly flag
	let filteredPages = $derived.by(() => {
		if (!query) return [];

		const lowerQuery = query.toLowerCase();
		let pages = docsOnly
			? sitemapPages.filter((p) => p.category === 'Documentation')
			: sitemapPages;

		// Simple fuzzy search
		return pages
			.map((page) => {
				const titleLower = page.title.toLowerCase();
				const descLower = page.description.toLowerCase();

				// Calculate match score
				let score = 0;

				// Exact title match gets highest score
				if (titleLower === lowerQuery) score += 100;
				else if (titleLower.includes(lowerQuery)) score += 50;

				// Description match
				if (descLower.includes(lowerQuery)) score += 25;

				// Word-by-word fuzzy matching
				const queryWords = lowerQuery.split(' ');
				queryWords.forEach((word) => {
					if (titleLower.includes(word)) score += 10;
					if (descLower.includes(word)) score += 5;
				});

				return { ...page, score };
			})
			.filter((p) => p.score > 0)
			.sort((a, b) => b.score - a.score)
			.slice(0, 8);
	});

	// Highlight matching text
	function highlightText(text: string, query: string): string {
		if (!query) return text;

		const lowerText = text.toLowerCase();
		const lowerQuery = query.toLowerCase();

		if (!lowerText.includes(lowerQuery)) {
			// Try word-by-word highlighting
			let result = text;
			query.split(' ').forEach((word) => {
				const regex = new RegExp(`(${word})`, 'gi');
				result = result.replace(
					regex,
					'<mark class="bg-yellow-200 text-gray-900 px-0.5 rounded">$1</mark>'
				);
			});
			return result;
		}

		const startIndex = lowerText.indexOf(lowerQuery);
		const endIndex = startIndex + query.length;

		return (
			text.substring(0, startIndex) +
			'<mark class="bg-yellow-200 text-gray-900 px-0.5 rounded">' +
			text.substring(startIndex, endIndex) +
			'</mark>' +
			text.substring(endIndex)
		);
	}

	const handleClose = (newOpen: boolean) => {
		open = newOpen;
		if (!newOpen) {
			query = '';
		}
		onOpenChange?.(newOpen);
	};

	function handleItemClick(url: string) {
		handleClose(false);
		goto(url);
	}

	function handleAskAI() {
		// TODO: Implement Ask Retain AI functionality
		console.log('Ask Retain AI:', query);
		handleClose(false);
	}
</script>

<Dialog bind:open onOpenChange={handleClose}>
	<DialogPortal>
		<DialogOverlay class="bg-black/5 backdrop-blur-[0.5px] dark:bg-black/20" />
		<DialogPrimitive.Content class="max-w-none border-0 bg-transparent">
			<div
				class="fixed left-1/2 top-[20%] z-[9999] grid w-full max-w-[600px] -translate-x-1/2 gap-4 overflow-hidden rounded-2xl border border-gray-200 bg-white p-0 shadow-2xl dark:border-gray-700 dark:bg-gray-900"
			>
				<Command class="pointer-events-auto" shouldFilter={false}>
					<div class="relative w-full border-b border-gray-200 dark:border-gray-700">
						<CommandInput
							placeholder={docsOnly ? 'Search documentation...' : 'Search Retain...'}
							bind:value={query}
							class="h-14 w-full border-0 px-4 text-base text-gray-900 placeholder:text-gray-400 focus:border-0 focus:outline-none focus:ring-0 dark:text-gray-100 dark:placeholder:text-gray-500"
						/>

						<DialogClose
							onclick={() => handleClose(false)}
							class="group absolute right-3 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-lg bg-gray-100 text-gray-500 transition-all hover:bg-gray-200 hover:text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
							aria-label="Close"
						>
							<IonIcon name="close-outline" myClass="size-4" />
						</DialogClose>
					</div>

					{#if query && filteredPages.length > 0}
						<CommandList class="max-h-[400px] p-2">
							<CommandGroup heading={docsOnly ? 'Documentation' : 'Results'}>
								{#each filteredPages as item (item.url)}
									<CommandItem
										value={item.url}
										onSelect={() => handleItemClick(item.url)}
										class="flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800"
									>
										<div
											class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-blue-500 to-cyan-600"
										>
											<IonIcon
												name={item.category === 'Documentation' ? 'book' : 'link'}
												myClass="size-4 text-white"
											/>
										</div>
										<div class="min-w-0 flex-1">
											<p class="text-sm font-medium text-gray-900 dark:text-gray-100">
												{@html highlightText(item.title, query)}
											</p>
											<p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
												{@html highlightText(item.description, query)}
											</p>
											<p class="mt-1 text-xs text-gray-400 dark:text-gray-500">
												{item.category}
											</p>
										</div>
									</CommandItem>
								{/each}
							</CommandGroup>

							<!-- Ask Retain AI Button -->
							<div class="border-t border-gray-200 p-2 dark:border-gray-700 hidden">
								<button
									onclick={handleAskAI}
									class="flex w-full items-center gap-3 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 px-3 py-2.5 text-white transition-all hover:from-purple-600 hover:to-blue-600"
								>
									<div
										class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-white/20"
									>
										<IonIcon name="sparkles" myClass="size-4 text-white" />
									</div>
									<div class="flex-1 text-left">
										<p class="text-sm font-medium">Ask Retain AI</p>
										<p class="text-xs text-white/80">Get AI-powered answers about "{query}"</p>
									</div>
								</button>
							</div>
						</CommandList>
					{/if}

					{#if query && filteredPages.length === 0}
						<CommandEmpty class="px-6 py-12 text-center">
							<div
								class="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800"
							>
								<IonIcon
									name="magnifyingglass"
									myClass="h-6 w-6 text-gray-500 dark:text-gray-400"
								/>
							</div>
							<p class="mt-4 text-base font-medium text-gray-900 dark:text-gray-100">
								No results found
							</p>
							<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
								Try a different search term or ask Retain AI
							</p>

							<!-- Ask Retain AI Button in empty state -->
							<button
								onclick={handleAskAI}
								class="mt-4 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 px-4 py-2 text-sm font-medium text-white transition-all hover:from-purple-600 hover:to-blue-600"
							>
								<IonIcon name="sparkles" myClass="size-4" />
								Ask Retain AI about "{query}"
							</button>
						</CommandEmpty>
					{/if}

					{#if !query}
						<div class="px-6 py-12 text-center">
							<div
								class="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800"
							>
								<IonIcon
									name="magnifyingglass"
									myClass="h-6 w-6 text-gray-500 dark:text-gray-400"
								/>
							</div>
							<p class="mt-4 text-base font-medium text-gray-900 dark:text-gray-100">
								{docsOnly ? 'Search Documentation' : 'Search Retain'}
							</p>
							<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
								{docsOnly
									? 'Find help articles, guides, and tutorials'
									: 'Search across all pages and documentation'}
							</p>
						</div>
					{/if}
				</Command>
			</div>
		</DialogPrimitive.Content>
	</DialogPortal>
</Dialog>
